import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Keyboard, Platform, RefreshControl, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useSelector} from 'react-redux';
import {useNavigation, useRoute} from '@react-navigation/native';

import {ExitPopup} from '/component/base/ExitPopup';
import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Icon from '/component/base/Icon';
import KeyboardInput from '/component/base/KeyboardInput';
import RadioButtonGroup from '/component/base/RadioButton/RadioButtonGroup';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import KeyboardAvoidingView from '/component/layout/KeyboardAvoidingView';
import ModalHeader from '/component/layout/ModalHeader';
import {HealthcareProviderTestIDs} from '/constant/TestIDs';
import {usePopup} from '/provider/PopupProvider';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';
import {
  selectHealthCareProviderFields,
  selectUsStates,
} from '/redux/selector/remoteConfig.selector';

import {SectionLoader} from '../ManageMySite/ManageMySiteViewScreen/ManageMySiteViewScreen';
import {StaticFocusable} from '../ManageMySite/sections/StaticFocusable';
import {StickyActionFooter} from '../ManageMySite/StickyActionFooter/StickyActionFooter';
import {BaseDropdown} from '../ManageMySite/widgets/BaseDropdown';
import {ErrorBanner} from '../ManageMySite/widgets/ErrorBanner/ErrorBanner';

import {useHealthcareProvider} from './HealthcareProviderScreen.hook';
import {styles} from './HealthcareProviderScreen.style';
import {
  HealthcareProviderModalProps,
  HealthcareProviderProps,
  SavePayload,
} from './HealthcareProviderScreen.types';

type RouteParams = {
  modalProps?: HealthcareProviderModalProps;
};

export const HealthcareProviderScreen = ({
  healthcareSaving = false,
  hasHealthcareData = false,
  hasHealthcareError = false,
  getHealthcareData,
  healthcareData,
}: HealthcareProviderProps) => {
  // @ts-expect-error popup typing in app
  const {showPopup, hidePopup} = usePopup();
  const navigation = useNavigation<any>();
  const route = useRoute<any>() as {params?: RouteParams};
  const scrollRef = useRef<KeyboardAwareScrollView | null>(null);
  const licenseInputRef = useRef<any>(null);

  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const modalProps = route.params?.modalProps ?? {};
  const stateOptions = useSelector(selectUsStates);
  const registeredFieldOptions = useSelector(selectHealthCareProviderFields);

  const shouldCloseWebModal = Platform.OS === 'web' && !modalProps?.onSave;

  if (shouldCloseWebModal) {
    navigation.goBack();
    return null;
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const handleRefresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      await getHealthcareData?.();
    } finally {
      setIsRefreshing(false);
    }
  }, [getHealthcareData]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!hasHealthcareData) {
      getHealthcareData?.();
    }
  }, [hasHealthcareData, getHealthcareData]);

  const hasModalInitialData =
    modalProps.initialIsHealthcareProvider !== undefined;

  const initialIsHealthcareProvider = hasModalInitialData
    ? modalProps.initialIsHealthcareProvider
    : healthcareData?.isHealthcareProvider ?? null;

  const initialRegisteredField = hasModalInitialData
    ? modalProps.initialRegisteredField ?? ''
    : healthcareData?.registeredField ?? '';

  const initialRegisteredState = hasModalInitialData
    ? modalProps.initialRegisteredState ?? ''
    : healthcareData?.registeredState ?? '';

  const initialLicenseNumber = hasModalInitialData
    ? modalProps.initialLicenseNumber ?? ''
    : healthcareData?.licenseNumber ?? '';

  const initialOtherRegisteredField = hasModalInitialData
    ? modalProps.initialOtherRegisteredField ?? ''
    : healthcareData?.otherRegisteredField ?? '';

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, () => {
      setIsKeyboardVisible(true);
    });

    const hideSub = Keyboard.addListener(hideEvent, () => {
      setIsKeyboardVisible(false);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const handleClose = (): void => {
    if (modalProps.onClose) {
      modalProps.onClose();
      return;
    }

    navigation.goBack();
  };

  const isBusy = isSubmitting || healthcareSaving || modalProps.isSaving;

  const handleSavePayload = async (payload: SavePayload): Promise<void> => {
    try {
      setIsSubmitting(true);

      await modalProps.onSave?.(payload);

      navigation.goBack();
    } catch (err) {
      setIsSubmitting(false);
      throw err;
    }
  };
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const {values, derived, actions, options} = useHealthcareProvider({
    initialIsHealthcareProvider,
    initialRegisteredField,
    initialRegisteredState,
    initialLicenseNumber,
    initialOtherRegisteredField,
    onSave: handleSavePayload,
    registeredFieldOptions,
    stateOptions,
    scrollRef,
  });

  const handleSave = () => {
    actions.handleSave();
  };

  const shouldShowFooter = !isKeyboardVisible && derived.isDirty;

  const androidKeyboardExtraHeight = derived.showOtherField ? 180 : 220;

  const outerBottomPadding =
    Platform.OS === 'web'
      ? 0
      : shouldShowFooter
      ? 0
      : Platform.OS === 'android'
      ? 80
      : 50;

  const handleBackPress = (): void => {
    if (isBusy) {
      return;
    }

    if (!derived.isDirty) {
      handleClose();
      return;
    }

    showPopup(
      ExitPopup,
      {
        title: 'Are you sure you want to go back?',
        description: 'All unsaved changes will be lost.',
        alignButtonsAsColumn: true,
        handleCancelButton: hidePopup,
        handleDiscardButton: async () => {
          await hidePopup();
          actions.resetState();
          handleClose();
        },
        exitButton: hidePopup,
        discardButtonStyle: {backgroundColor: '#000'},
      },
      {avoidable: false, showFullModal: false},
    );
  };

  const handleCancelChanges = (): void => {
    Keyboard.dismiss();
    actions.resetState();
  };

  const scrollToBottom = (): void => {
    setTimeout(() => {
      // @ts-ignore - KeyboardAwareScrollView types are incorrect
      scrollRef.current?.scrollToEnd({animated: true});
    }, 250);
  };

  const selectedStateLabel =
    stateOptions.find(opt => opt.value === values.registeredState)?.label || '';

  const registeredFieldAccessibilityLabel = values.registeredField
    ? `Registered healthcare provider field, ${
        values.registeredField
      }, selected${!derived.fieldsEnabled ? ', disabled' : ''}`
    : `Registered healthcare provider field, Select Registered Field${
        !derived.fieldsEnabled ? ', disabled' : ''
      }`;

  const registeredStateAccessibilityLabel = values.registeredState
    ? `Registered state, ${selectedStateLabel}, selected${
        !derived.fieldsEnabled ? ', disabled' : ''
      }`
    : `Registered state, Select Registered State${
        !derived.fieldsEnabled ? ', disabled' : ''
      }`;

  const registeredFieldAccessibilityHint = derived.fieldsEnabled
    ? 'Double tap to open registered field options'
    : 'Disabled. Select Yes above to enable';

  const registeredStateAccessibilityHint = derived.fieldsEnabled
    ? 'Double tap to open registered state options'
    : 'Disabled. Select Yes above to enable';

  const errorCount = Object.values(values.errors).filter(Boolean).length;

  const isInitialLoading = !hasModalInitialData && !hasHealthcareData;

  if (hasHealthcareError && !hasHealthcareData) {
    return (
      <SafeAreaView style={styles.errorScreen}>
        <TouchableOpacity
          style={styles.errorCard}
          onPress={handleClose}
          accessible
          accessibilityRole="button"
          accessibilityLabel="Unable to load healthcare provider details. Please try again later."
          accessibilityHint="Double tap to go back"
          testID={HealthcareProviderTestIDs.errorContainer}>
          <View
            style={styles.errorContent}
            accessible={false}
            importantForAccessibility="no-hide-descendants">
            <Icon name="info" style={styles.errorIcon} accessible={false} />
            <Text
              style={styles.errorTitle}
              allowFontScaling
              maxFontSizeMultiplier={2}
              testID={HealthcareProviderTestIDs.errorTitle}>
              Unable to load healthcare details
            </Text>
            <Text
              style={styles.errorSubtitle}
              testID={HealthcareProviderTestIDs.errorSubTitle}>
              Please try again later.
            </Text>
          </View>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <ModalHeader
      title={tt('healthCareProvider', 'Health Care Provider')}
      accessibilityLabel="Health Care Provider"
      accessibilityHint="Go back to previous screen"
      onClose={handleBackPress}
      closeIconName="chevron-left"
      style={{base: styles.base, headerText: styles.headerText}}
      useAccessibleTitleContainer>
      {isInitialLoading ? (
        <SectionLoader label="Loading healthcare provider data" />
      ) : (
        <KeyboardAvoidingView
          style={styles.scrollViewBase}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View
            style={[
              styles.scrollViewBase,
              {
                paddingBottom: outerBottomPadding,
              },
            ]}
            pointerEvents={isBusy ? 'none' : 'auto'}>
            <KeyboardAwareScrollView
              refreshControl={
                <RefreshControl
                  refreshing={isRefreshing}
                  onRefresh={handleRefresh}
                />
              }
              ref={scrollRef}
              style={styles.scrollViewBase}
              contentContainerStyle={[
                styles.content,
                Platform.OS === 'web'
                  ? {paddingTop: 60, paddingBottom: 16}
                  : {flexGrow: 1},
              ]}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode="on-drag"
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled
              scrollEventThrottle={16}
              enableOnAndroid
              enableAutomaticScroll={Platform.OS === 'android'}
              enableResetScrollToCoords={false}
              extraScrollHeight={
                Platform.OS === 'android' ? androidKeyboardExtraHeight : 0
              }
              extraHeight={
                Platform.OS === 'android' ? androidKeyboardExtraHeight : 0
              }>
              <View
                style={[
                  styles.cardWrapper,
                  derived.hasSubmitted && errorCount > 0
                    ? styles.cardWrapperWithError
                    : styles.cardWrapperWithoutError,
                ]}>
                <View style={styles.introCard}>
                  <StaticFocusable
                    testID={HealthcareProviderTestIDs.introTitle}
                    role="header"
                    label={String(
                      tt(
                        'healthcareProviderIntroTitle',
                        'Healthcare Provider Information',
                      ),
                    )}>
                    <Text
                      isBold
                      style={styles.sectionTitle}
                      accessible={false}
                      allowFontScaling
                      maxFontSizeMultiplier={2}
                      testID={HealthcareProviderTestIDs.introTitleText}>
                      {tt(
                        'healthcareProviderIntroTitle',
                        'Healthcare Provider Information',
                      )}
                    </Text>
                  </StaticFocusable>

                  <StaticFocusable
                    testID={HealthcareProviderTestIDs.introDescription}
                    label={String(
                      tt(
                        'healthcareProviderIntroText',
                        'Tell us whether you are a healthcare provider. If yes, please provide your registration details below.',
                      ),
                    )}>
                    <Text
                      style={styles.sectionDescription}
                      accessible={false}
                      allowFontScaling
                      maxFontSizeMultiplier={2}
                      testID={HealthcareProviderTestIDs.introDescriptionText}>
                      {tt(
                        'healthcareProviderIntroText',
                        'Tell us whether you are a healthcare provider. If yes, please provide your registration details below.',
                      )}
                    </Text>
                  </StaticFocusable>

                  <View style={styles.questionBlock}>
                    <RadioButtonGroup
                      allowFontScaling
                      maxFontSizeMultiplier={2}
                      useAccessibleTitleWrapper
                      title={String(
                        tt(
                          'isHealthcareProviderQuestion',
                          'Are you a Healthcare Provider?',
                        ),
                      )}
                      titleStyle={{fontSize: 14}}
                      options={[
                        {
                          text: String(tt('yes', 'Yes')),
                          selected: values.isHealthcareProvider === true,
                          onPress: () =>
                            actions.handleHealthcareProviderChange(true),
                          testID: HealthcareProviderTestIDs.yesOption,
                          subText: '',
                          accessibilityLabel: 'Yes, I am a healthcare provider',
                          accessibilityHint:
                            'Select this if you are a registered healthcare provider',
                        },
                        {
                          text: String(tt('no', 'No')),
                          selected: values.isHealthcareProvider === false,
                          onPress: () =>
                            actions.handleHealthcareProviderChange(false),
                          testID: HealthcareProviderTestIDs.noOption,
                          subText: '',
                          accessibilityLabel:
                            'No, I am not a healthcare provider',
                          accessibilityHint:
                            'Select this if you do not have healthcare registration details',
                        },
                      ]}
                    />
                  </View>
                </View>
              </View>

              <ErrorBanner
                testID={HealthcareProviderTestIDs.errorBanner.container}
                errorCount={errorCount}
                hasSubmitted={derived.hasSubmitted}
              />

              <View
                style={[
                  styles.detailsCard,
                  !derived.fieldsEnabled && styles.detailsCardDisabled,
                ]}
                accessible={false}>
                <StaticFocusable
                  testID={HealthcareProviderTestIDs.detailsHeader}
                  role="header"
                  label={String(
                    `${tt('providerDetails', 'Healthcare Provider Details')}${
                      !derived.fieldsEnabled ? ', disabled' : ''
                    }. header, Select Yes above to enable this section.`,
                  )}
                  state={{disabled: !derived.fieldsEnabled}}
                  importantForAccessibility="yes">
                  <Text
                    style={styles.detailsTitle}
                    accessible={false}
                    allowFontScaling
                    maxFontSizeMultiplier={2}
                    testID={HealthcareProviderTestIDs.detailsTitleText}>
                    {tt('providerDetails', 'Healthcare Provider Details')}
                  </Text>
                </StaticFocusable>

                <View accessible={false}>
                  <View
                    accessible
                    testID={HealthcareProviderTestIDs.detailsSubtitle}
                    accessibilityLabel={String(
                      tt(
                        'providerDetailsSubtitle',
                        'Complete the registration details below.',
                      ),
                    )}>
                    <Text
                      style={styles.detailsSubtitle}
                      accessible={false}
                      allowFontScaling
                      maxFontSizeMultiplier={2}
                      testID={HealthcareProviderTestIDs.detailsSubtitleText}>
                      {tt(
                        'providerDetailsSubtitle',
                        'Complete the registration details below.',
                      )}
                    </Text>
                  </View>

                  <View style={styles.fieldRow} accessible={false}>
                    <View
                      accessible
                      testID={HealthcareProviderTestIDs.registeredFieldLabel}
                      accessibilityLabel={
                        String(
                          tt(
                            'registeredFieldLabel',
                            'What field are you registered in?',
                          ),
                        ) + (derived.fieldsEnabled ? ', required' : '')
                      }>
                      <Text
                        style={styles.fieldLabel}
                        accessible={false}
                        allowFontScaling
                        maxFontSizeMultiplier={2}
                        testID={
                          HealthcareProviderTestIDs.registeredFieldLabelText
                        }>
                        {tt(
                          'registeredFieldLabel',
                          'What field are you registered in?',
                        )}
                        <Text style={styles.required}> *</Text>
                      </Text>
                    </View>

                    <BaseDropdown
                      accessible
                      accessibilityRole="button"
                      dropdownKey="registeredField"
                      isOpen={values.activeDropdown === 'registeredField'}
                      onToggle={actions.handleToggle}
                      value={values.registeredField}
                      onChange={(value: string) => {
                        actions.handleRegisteredFieldChange(value);
                        actions.closeDropdown();
                      }}
                      options={options.registeredFields}
                      placeholder={String(
                        tt(
                          'selectRegisteredFields',
                          'Select Registered Fields',
                        ),
                      )}
                      hasError={!!values.errors.registeredField}
                      errorText={values.errors.registeredField}
                      disabled={!derived.fieldsEnabled}
                      accessibilityState={{disabled: !derived.fieldsEnabled}}
                      accessibilityLabel={
                        registeredFieldAccessibilityLabel ||
                        'Registered healthcare provider field'
                      }
                      accessibilityHint={registeredFieldAccessibilityHint}
                      testID={HealthcareProviderTestIDs.registeredFieldDropdown}
                    />
                  </View>

                  {derived.showOtherField && (
                    <View style={styles.fieldRow}>
                      <View
                        accessible
                        testID={
                          HealthcareProviderTestIDs.otherFieldLabelContainer
                        }
                        accessibilityLabel={
                          String(
                            tt(
                              'otherRegisteredFieldLabel',
                              'Please specify your healthcare provider field',
                            ),
                          ) + (derived.fieldsEnabled ? ', required' : '')
                        }>
                        <Text
                          style={styles.fieldLabel}
                          accessible={false}
                          allowFontScaling
                          maxFontSizeMultiplier={2}
                          testID={
                            HealthcareProviderTestIDs.otherFieldLabelText
                          }>
                          {tt(
                            'otherRegisteredFieldLabel',
                            'Please specify your healthcare provider field',
                          )}
                        </Text>
                      </View>

                      <View
                        style={[
                          styles.inputRow,
                          values.errors.otherRegisteredField &&
                            styles.inputRowError,
                        ]}>
                        <KeyboardInput
                          style={styles.textInput}
                          value={values.otherRegisteredField}
                          onChangeText={actions.setOtherRegisteredField}
                          onFocus={scrollToBottom}
                          placeholderTextColor="#9CA3AF"
                          placeholder={String(
                            tt(
                              'otherRegisteredFieldPlaceholder',
                              'Enter healthcare provider field',
                            ),
                          )}
                          autoCorrect={false}
                          maxLength={40}
                          returnKeyType="done"
                          allowFontScaling={true}
                          maxFontSizeMultiplier={2}
                          accessibilityLabel="Other healthcare provider field"
                          accessibilityHint="Enter your healthcare provider field"
                          accessibilityState={{
                            disabled: !derived.fieldsEnabled,
                          }}
                          editable={derived.fieldsEnabled}
                          testID={HealthcareProviderTestIDs.otherFieldInput}
                        />
                      </View>

                      {!!values.errors.otherRegisteredField && (
                        <View
                          accessible
                          testID={HealthcareProviderTestIDs.otherFieldError}
                          accessibilityRole="alert"
                          accessibilityLabel={`Error: ${values.errors.otherRegisteredField}`}>
                          <Text
                            style={styles.fieldError}
                            accessible={false}
                            allowFontScaling
                            maxFontSizeMultiplier={2}
                            testID={
                              HealthcareProviderTestIDs.otherFieldErrorText
                            }>
                            {values.errors.otherRegisteredField}
                          </Text>
                        </View>
                      )}
                    </View>
                  )}

                  <View style={styles.fieldRow}>
                    <View
                      accessible
                      testID={HealthcareProviderTestIDs.registeredStateLabel}
                      accessibilityLabel={
                        String(
                          tt(
                            'registeredStateLabel',
                            'What state are you registered in?',
                          ),
                        ) + (derived.fieldsEnabled ? ', required' : '')
                      }>
                      <Text
                        style={styles.fieldLabel}
                        accessible={false}
                        allowFontScaling
                        maxFontSizeMultiplier={2}
                        testID={
                          HealthcareProviderTestIDs.registeredStateLabelText
                        }>
                        {tt(
                          'registeredStateLabel',
                          'What state are you registered in?',
                        )}
                        <Text
                          style={styles.required}
                          allowFontScaling
                          maxFontSizeMultiplier={2}>
                          {' '}
                          *
                        </Text>
                      </Text>
                    </View>

                    <BaseDropdown
                      accessible
                      accessibilityRole="button"
                      dropdownKey="registeredState"
                      isOpen={values.activeDropdown === 'registeredState'}
                      onToggle={actions.handleToggle}
                      value={values.registeredState}
                      onChange={(value: string) => {
                        actions.setRegisteredState(value);
                        actions.closeDropdown();
                      }}
                      options={options.states}
                      placeholder={String(
                        tt('selectState', 'Select Registered State'),
                      )}
                      hasError={!!values.errors.registeredState}
                      errorText={values.errors.registeredState}
                      disabled={!derived.fieldsEnabled}
                      accessibilityState={{disabled: !derived.fieldsEnabled}}
                      accessibilityLabel={
                        registeredStateAccessibilityLabel || 'Registered state'
                      }
                      accessibilityHint={registeredStateAccessibilityHint}
                      testID={HealthcareProviderTestIDs.registeredStateDropdown}
                    />
                  </View>

                  <View style={styles.fieldRowLast}>
                    <StaticFocusable
                      testID={HealthcareProviderTestIDs.licenseLabel}
                      role="text"
                      label={
                        String(
                          tt(
                            'licenseNumberLabel',
                            'What is your license Number?',
                          ),
                        ) + (derived.fieldsEnabled ? ', required' : '')
                      }>
                      <Text
                        style={styles.fieldLabel}
                        accessible={false}
                        allowFontScaling
                        maxFontSizeMultiplier={2}
                        testID={HealthcareProviderTestIDs.licenseLabelText}>
                        {tt(
                          'licenseNumberLabel',
                          'What is your license Number?',
                        )}
                        <Text style={styles.required}> *</Text>
                      </Text>
                    </StaticFocusable>

                    <View
                      style={[
                        styles.inputRow,
                        values.errors.licenseNumber && styles.inputRowError,
                      ]}>
                      {derived.fieldsEnabled ? (
                        <KeyboardInput
                          ref={licenseInputRef}
                          style={styles.textInput}
                          value={values.licenseNumber}
                          onChangeText={actions.setLicenseNumber}
                          onFocus={scrollToBottom}
                          placeholderTextColor="#667085"
                          placeholder={String(
                            tt(
                              'licenseNumberPlaceholder',
                              'Enter registration license number',
                            ),
                          )}
                          autoCapitalize="none"
                          autoCorrect={false}
                          maxLength={40}
                          returnKeyType="done"
                          submitBehavior="submit"
                          allowFontScaling
                          maxFontSizeMultiplier={2}
                          accessibilityLabel={
                            values.licenseNumber
                              ? `Value, ${values.licenseNumber}`
                              : 'Input field, empty'
                          }
                          accessibilityHint="Enter your registration license number"
                          testID={HealthcareProviderTestIDs.licenseInput}
                        />
                      ) : (
                        <KeyboardFocusable
                          style={styles.textInputReadonlyWrapper}
                          accessible
                          accessibilityRole="text"
                          accessibilityLabel={`Registration license number, ${
                            values.licenseNumber || 'Not provided'
                          }, disabled`}
                          accessibilityHint="This field is disabled. Select Yes above to enable"
                          accessibilityState={{disabled: true}}
                          testID={
                            HealthcareProviderTestIDs.licenseReadyOnlyInput
                          }>
                          <Text
                            style={styles.textInputReadonly}
                            allowFontScaling
                            maxFontSizeMultiplier={2}
                            accessible={false}
                            testID={
                              HealthcareProviderTestIDs.licenseReadyOnlyInputText
                            }>
                            {values.licenseNumber ||
                              tt(
                                'licenseNumberPlaceholder',
                                'Enter registration license number',
                              )}
                          </Text>
                        </KeyboardFocusable>
                      )}
                    </View>

                    {!!values.errors.licenseNumber && (
                      <View
                        accessible
                        testID={HealthcareProviderTestIDs.licenseError}
                        accessibilityRole="alert"
                        accessibilityLabel={`Error: ${values.errors.licenseNumber}`}>
                        <Text
                          style={styles.fieldError}
                          accessible={false}
                          allowFontScaling
                          maxFontSizeMultiplier={2}
                          testID={HealthcareProviderTestIDs.licenseErrorText}>
                          {values.errors.licenseNumber}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
            </KeyboardAwareScrollView>

            {isBusy && (
              <View style={styles.containerOverlay} pointerEvents="box-only" />
            )}

            {shouldShowFooter && (
              <StickyActionFooter
                onCancel={isBusy ? () => {} : handleCancelChanges}
                onSave={handleSave}
                isSaving={isBusy}
                showSave
                showCancel
                disableSave={isBusy || !derived.isDirty}
                saveTestID={HealthcareProviderTestIDs.saveButton}
                saveLabel={String(tt('save', 'Save'))}
              />
            )}
          </View>
        </KeyboardAvoidingView>
      )}
    </ModalHeader>
  );
};
